export const BUSINESS_DAY_START_SECONDS = 8 * 60 * 60
export const BUSINESS_DAY_END_SECONDS = 17 * 60 * 60 + 30 * 60

export type FilterKey = 'OK' | 'Retirar'
export type PeriodStatus = 'Prévia' | 'Fechado'

export interface ParsedDateTime {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  weekday: number
  timeSeconds: number
  timeFraction: number
  excelDate: number
}

export function normalizeHeader(value: unknown): string {
  return String(value ?? '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[\r\n\t]+/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function hasValidDateParts(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): boolean {
  if (
    year < 1900 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return false
  }

  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}

function createDateTime(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): ParsedDateTime | null {
  if (!hasValidDateParts(year, month, day, hour, minute, second)) {
    return null
  }

  const dateUtc = Date.UTC(year, month - 1, day)
  const timeSeconds = hour * 3600 + minute * 60 + second

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    weekday: new Date(dateUtc).getUTCDay(),
    timeSeconds,
    timeFraction: timeSeconds / 86400,
    excelDate: dateUtc / 86400000 + 25569,
  }
}

export function parseLiteralDateTime(value: unknown): ParsedDateTime | null {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null
  }

  const text = String(value).trim()
  let match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?/,
  )

  if (match) {
    return createDateTime(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
      Number(match[4] ?? 0),
      Number(match[5] ?? 0),
      Number(match[6] ?? 0),
    )
  }

  match = text.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  )

  if (!match) {
    return null
  }

  return createDateTime(
    Number(match[3]),
    Number(match[2]),
    Number(match[1]),
    Number(match[4] ?? 0),
    Number(match[5] ?? 0),
    Number(match[6] ?? 0),
  )
}

export function calculateFilterKey(dateTime: ParsedDateTime | null): FilterKey {
  if (!dateTime) {
    return 'Retirar'
  }

  const isWeekday = dateTime.weekday !== 0 && dateTime.weekday !== 6
  const isBusinessHours =
    dateTime.timeSeconds >= BUSINESS_DAY_START_SECONDS &&
    dateTime.timeSeconds <= BUSINESS_DAY_END_SECONDS

  return isWeekday && isBusinessHours ? 'OK' : 'Retirar'
}

export function parseFilledMetric(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null
  }

  const text = String(value).trim()
  if (text === '' || text === '-') {
    return null
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (!/^[-+]?\d+(?:[.,]\d+)?$/.test(text)) {
    return null
  }

  const parsed = Number(text.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

export function averageFilledMetrics(values: unknown[]): number | null {
  const validValues = values
    .map(parseFilledMetric)
    .filter((value): value is number => value !== null)

  if (validValues.length === 0) {
    return null
  }

  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length
}

export function normalizeQueue(value: unknown): string {
  const queue = String(value ?? '').trim()
  const aliases: Record<string, string> = {
    rc: 'RC',
    comercial: 'Comercial',
    'comercial viva hau': 'Comercial VIVA HAU',
    contrato: 'Contrato',
    default: 'Default',
    _default_: 'Default',
  }

  return aliases[queue.toLowerCase()] ?? (queue || 'Sem fila')
}

export function queueContributesToTimeMetrics(queue: unknown): boolean {
  return normalizeQueue(queue) !== 'Default'
}

export function canReplacePeriod(status: PeriodStatus | null): boolean {
  return status !== 'Fechado'
}
